import {
  Box,
  Divider,
  Button,
  Grid,
  useToast,
  Center,
  Spinner,
} from '@chakra-ui/react';
import { useContext, useState, useEffect } from 'react';
import PassDownloadTemplateContext from '../../../store/client/PassDownloadTemplateContext';
import { MeContext } from '../../../context/MeContext';
import { useIsReadOnly } from '../../../hooks/useIsReadOnly';
import LockedActionButton from '../../common/LockedActionButton';
import PreviewDownloadScreens from './PreviewDownloadScreens';
import PassDownloadTemplateDetails from './PassDownloadTemplateDetails';
import { HttpContext } from '../../../context/HttpContext';
import CustomToast from '../../../common/CustomToast';
import { useNavigate, useParams } from 'react-router';
import * as yup from 'yup';
import { dashboardMutation } from '../../../utils/dashboardMutation';

const PassDownloadTemplateForm = ({ isEdit }) => {
  const isReadOnly = useIsReadOnly();
  const readOnlyInertProps = isReadOnly ? { inert: '' } : {};
  const { uuid } = useParams();
  const { authAxios } = useContext(HttpContext);
  const meCtx = useContext(MeContext);
  const { entities, filterEntity } = meCtx.state;
  const passDownloadCtx = useContext(PassDownloadTemplateContext);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [defaultEntityPage, setDefaultEntityPage] = useState(false);
  const [selectedPassTemplates, setSelectedPassTemplates] = useState([]);
  const [translationErrors, setTranslationErrors] = useState({});
  const [forcedOpenLanguages, setForcedOpenLanguages] = useState([]);
  const [editingLang, setEditingLang] = useState(null);

  const [showHelpAndFAQs, setShowHelpAndFAQs] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    if (showHelpAndFAQs === false) {
      passDownloadCtx.updateClientHelpLink({
        display_text: '',
        url: '',
      });
      if (passDownloadCtx.translations) {
        Object.keys(passDownloadCtx.translations).forEach((lang) => {
          passDownloadCtx.updateTranslations(lang, 'clientHelpLink', '');
        });
      }
    }
  }, [showHelpAndFAQs]);

  const passDownloadSchema = yup
    .object()
    .shape({
      name: yup.string().trim().required('Page name is required'),
      successMessage: yup
        .string()
        .trim()
        .required('Success message is required'),
      pageTitle: yup.string().trim().required('Page title is required'),
      selectedPassTemplates: yup
        .array()
        .when('$validateSelectedPassTemplates', {
          is: true,
          then: (schema) =>
            schema.min(
              1,
              'At least one pass template must be assigned to the page'
            ),
          otherwise: (schema) => schema.notRequired(),
        }),
    })
    .test(
      'default-or-template-required',
      'Either the default page must be selected or at least one pass template assigned',
      function (values) {
        const { defaultEntityPage, selectedPassTemplates } =
          this.options.context;
        const isEdit = this.options.context.isEdit;

        if (
          !isEdit &&
          !defaultEntityPage &&
          (!selectedPassTemplates || selectedPassTemplates.length === 0)
        ) {
          return this.createError({
            message:
              'Either the default entity page must be selected or at least one pass template assigned',
          });
        }

        return true;
      }
    );

  const getActiveTranslationFields = () => {
    const fields = [];
    if (passDownloadCtx.headerText?.trim()) fields.push('headerText');
    if (passDownloadCtx.passDownloadText?.trim())
      fields.push('passDownloadText');
    if (passDownloadCtx.successMessage?.trim()) fields.push('successMessage');
    if (passDownloadCtx.pageTitle?.trim()) fields.push('pageTitle');
    if (passDownloadCtx.clientHelpLink?.display_text?.trim())
      fields.push('clientHelpLink');

    return fields;
  };

  const getTranslationErrors = () => {
    const errors = {};
    const activeFields = getActiveTranslationFields();

    Object.entries(passDownloadCtx.translations).forEach(([lang, fields]) => {
      const fieldErrors = {};
      activeFields.forEach((field) => {
        if (!fields[field]?.trim()) {
          fieldErrors[field] = true;
        }
      });

      if (Object.keys(fieldErrors).length > 0) {
        errors[lang] = fieldErrors;
      }
    });

    return errors;
  };

  const sanitizeTranslations = (translations, headerText, passDownloadText) => {
    const sanitizedTranslations = JSON.parse(JSON.stringify(translations));
    const shouldRemoveHeader = !headerText?.trim();
    const shouldRemoveBody = !passDownloadText?.trim();

    if (shouldRemoveHeader || shouldRemoveBody) {
      Object.keys(sanitizedTranslations).forEach((lang) => {
        if (shouldRemoveHeader) {
          delete sanitizedTranslations[lang].headerText;
        }
        if (shouldRemoveBody) {
          delete sanitizedTranslations[lang].passDownloadText;
        }
      });
    }
    return sanitizedTranslations;
  };

  const prepareDataForSubmit = () => {
    const {
      name,
      backgroundColor,
      headerText,
      headerColor,
      passDownloadText,
      passDownloadTextColor,
      clientWebsite,
      pageTitle,
      skipPreview,
      successMessage,
      logoImage,
      faviconImage,
      componentFillColor,
      componentFontColor,
      translations,
    } = passDownloadCtx;

    let entityId = [];

    if (isEdit && passDownloadCtx.entities.length > 0) {
      const entity = entities.find(
        (entity) => entity.uuid === passDownloadCtx.entities[0]
      );
      entityId = [entity.id];
    }

    const sanitizedTranslations = sanitizeTranslations(
      translations,
      headerText,
      passDownloadText
    );

    const submittedData = {
      name: name,
      background_color: backgroundColor,
      header_text: headerText,
      header_color: headerColor,
      pass_download_text: passDownloadText,
      pass_download_text_color: passDownloadTextColor,
      client_website: clientWebsite,
      page_title: pageTitle,
      skip_preview: skipPreview,
      success_message: successMessage,
      logo_image: logoImage,
      favicon_image: faviconImage,
      component_fill_color: componentFillColor,
      component_font_color: componentFontColor,
      entity_ids:
        isEdit && passDownloadCtx.entities.length > 0
          ? entityId
          : passDownloadCtx.entities,
      pass_template_ids: selectedPassTemplates.map(
        (passTemplate) => passTemplate.value
      ),
      translations: sanitizedTranslations,
      client_help_link: !showHelpAndFAQs
        ? { display_text: '', url: '' }
        : {
            display_text: passDownloadCtx.clientHelpLink?.display_text,
            url: passDownloadCtx.clientHelpLink?.url,
          },
    };
    return submittedData;
  };

  const handleEdit = async () => {
    setIsSubmitting(true);
    const submittedData = prepareDataForSubmit();
    await dashboardMutation(
      async () => {
        const { data: responseData } = await authAxios.patch(
          `api/v1/pass_download_templates/${uuid}`,
          submittedData
        );
        passDownloadCtx.updateAllData(responseData.data?.attributes);
      },
      {
        action: 'updateDownloadPage',
        toast,
        successMessage: 'Pass download page updated successfully',
        errorTitle: 'Failed to update download page',
        onSuccess: () => navigate('/pass-download-pages'),
        onError: () => setIsSubmitting(false),
      }
    );
  };

  const onSubmit = async () => {
    const { name, successMessage, pageTitle } = passDownloadCtx;
    try {
      await passDownloadSchema.validate(
        {
          name,
          successMessage,
          pageTitle,
          selectedPassTemplates,
        },
        {
          abortEarly: false,
          context: {
            validateSelectedPassTemplates:
              isEdit &&
              passDownloadCtx.entities.length === 0 &&
              !passDownloadCtx.defaultForOrg,
            defaultEntityPage,
            selectedPassTemplates,
            isEdit,
          },
        }
      );
      const translationValidationErrors = getTranslationErrors();

      if (Object.keys(translationValidationErrors).length > 0) {
        setTranslationErrors(translationValidationErrors);
        setForcedOpenLanguages(Object.keys(translationValidationErrors));

        toast({
          render: ({ onClose }) => (
            <CustomToast
              status="error"
              title={`Missing language translations`}
              description="Please fill out all required fields."
              onClose={onClose}
            />
          ),
        });

        return;
      }
      setTranslationErrors({});
      setForcedOpenLanguages([]);
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        err.errors.forEach((message) => {
          toast({
            render: ({ onClose }) => (
              <CustomToast status="error" title={message} onClose={onClose} />
            ),
          });
        });
        return;
      }
    }
    if (isEdit) {
      handleEdit();
      return;
    }
    setIsSubmitting(true);
    const submittedData = prepareDataForSubmit();
    await dashboardMutation(
      () => authAxios.post(`api/v1/pass_download_templates`, submittedData),
      {
        action: 'createDownloadPage',
        toast,
        successMessage: 'Pass download page created successfully',
        errorTitle: 'Failed to create download page',
        onSuccess: async () => {
          if (filterEntity) {
            const { data: responseData } = await authAxios.get(`accounts/me`);
            const entities = responseData?.entities;
            meCtx.updateState({
              ...meCtx.state,
              entities: entities,
            });
          }
          navigate('/pass-download-pages');
        },
        onError: () => setIsSubmitting(false),
      }
    );
  };

  useEffect(() => {
    function determineFooterColor(hex) {
      const cleanHex = hex.replace(/^#/, '');
      const intVal = parseInt(cleanHex, 16);
      const r = (intVal >> 16) & 255;
      const g = (intVal >> 8) & 255;
      const b = intVal & 255;
      const average = (r + g + b) / 3;
      passDownloadCtx.updateFooterColor(average > 127 ? 'dark' : 'light');
    }
    if (!passDownloadCtx.backgroundColor) return;
    determineFooterColor(passDownloadCtx.backgroundColor);
  }, [passDownloadCtx.backgroundColor]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (passDownloadCtx && meCtx?.state?.entities) {
        setIsLoading(false);
      }
    }, 300); // 300ms debounce
    return () => clearTimeout(timer);
  }, [passDownloadCtx, meCtx.state.entities]);

  if (isLoading) {
    return (
      <Center>
        <Spinner
          width="52px"
          height="52px"
          thickness="4px"
          speed="0.65s"
          emptyColor="quinaryBackground"
          mt={4}
          mx="auto"
        />
      </Center>
    );
  }

  return (
    <>
      <Grid
        role="grid"
        templateColumns={{ base: '100%', xl: '1fr 1fr' }}
        gap={6}
      >
        <Box opacity={isReadOnly ? 0.6 : 1} {...readOnlyInertProps}>
          <PassDownloadTemplateDetails
            isEdit={isEdit}
            defaultEntityPage={defaultEntityPage}
            setDefaultEntityPage={setDefaultEntityPage}
            selectedPassTemplates={selectedPassTemplates}
            setSelectedPassTemplates={setSelectedPassTemplates}
            editingLang={editingLang}
            setEditingLang={setEditingLang}
            translationErrors={translationErrors}
            setTranslationErrors={setTranslationErrors}
            forcedOpenLanguages={forcedOpenLanguages}
            getActiveTranslationFields={getActiveTranslationFields()}
            showHelpAndFAQs={showHelpAndFAQs}
            setShowHelpAndFAQs={setShowHelpAndFAQs}
          />
        </Box>
        <PreviewDownloadScreens editingLang={editingLang} />
      </Grid>
      <Box mt={{ base: '30px' }} w="full">
        <Divider borderColor="primaryBackground" mb="15px" />
        {isReadOnly ? (
          <LockedActionButton reason="download-templates" size="sm" mr="24px">
            Save
          </LockedActionButton>
        ) : (
          <Button
            onClick={() => onSubmit()}
            isLoading={isSubmitting}
            alt="Save"
            size="sm"
            mr="24px"
          >
            Save
          </Button>
        )}
      </Box>
    </>
  );
};

export default PassDownloadTemplateForm;
